import React, { useCallback, useEffect, useState } from 'react';
import css from './settings-modal.module.css';
import {
  ButtonComponent,
  ButtonStyling,
} from '../../../../../components/form/elements/button';
import { useTranslation } from 'react-i18next';
import Table from '../../../../../components/common/table/table';
import { useService } from '../../../../../services/services';
import { useParams } from 'react-router-dom';
import AnimalConfiguration, {
  AnimalConfigurationPair,
} from '../../../../../interfaces/animal-configuration';
import { ListParams } from '../../../../../services/http-client';

interface Props {
  closeModal: () => void;
}

enum TabKey {
  CATTLE = 'Liellopi',
  SHEEPS = 'Aitas',
  GOATS = 'Kazas',
  HORSES = 'Zirgi',
}

enum eventType {
  SEARCHING = 'searching',
  DRY_OFF = 'dry_off',
  CALVING = 'calving',
  PREGNANCY = 'pregnancy',
}

const tabs = [
  {
    key: TabKey.CATTLE,
    label: 'pages.livestock.animals_and_events.filters.species.cattle',
  },
  {
    key: TabKey.SHEEPS,
    label: 'pages.livestock.animals_and_events.filters.species.sheep',
  },
  {
    key: TabKey.GOATS,
    label: 'pages.livestock.animals_and_events.filters.species.goats',
  },
  {
    key: TabKey.HORSES,
    label: 'pages.livestock.animals_and_events.filters.species.horses',
  },
];

export default function SettingsModal({ closeModal }: Props) {
  const { t } = useTranslation();
  const { farmId } = useParams<{ farmId: string }>();
  const { animalConfigurationService } = useService();
  const [inputValues, setInputValues] = useState<{
    [tabKey in TabKey]: { [key: string]: number };
  }>({
    [TabKey.CATTLE]: {},
    [TabKey.SHEEPS]: {},
    [TabKey.GOATS]: {},
    [TabKey.HORSES]: {},
  });
  const [configurations, setConfigurations] = useState<{
    [key: string]: number;
  }>({});

  const [selectedTab, setSelectedTab] = useState<TabKey>(TabKey.CATTLE);

  const handleTabChange = (key: TabKey) => {
    setSelectedTab(key);
  };

  const handleInputChange = useCallback(
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value.replace(/[^\d]/g, '');
      const value = input === '' ? 0 : parseInt(input, 10);

      setInputValues((prevValues) => ({
        ...prevValues,
        [selectedTab]: {
          ...prevValues[selectedTab],
          [id]: value,
        },
      }));
    },
    [selectedTab]
  );

  const handleSave = () => {
    const saveData = Object.values(TabKey).flatMap((tabKey) =>
      Object.entries(inputValues[tabKey]).map(([id, value]) => ({
        id,
        value,
      }))
    );

    animalConfigurationService
      .saveConfigurations(farmId, saveData)
      .subscribe(() => {
        closeModal();
      });
  };

  const renderGoalPicker = (row: AnimalConfiguration) => {
    const currentValue =
      inputValues[selectedTab][row.id] !== undefined
        ? inputValues[selectedTab][row.id]
        : configurations[row.id] || row.day_count;

    return (
      <input
        className={css['goal-picker']}
        type="text"
        value={currentValue}
        onChange={handleInputChange(row.id)}
      />
    );
  };

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) =>
      animalConfigurationService.index(farmId, {
        page,
        sort,
        desc,
        ...filter,
        species: selectedTab,
      }),
    [animalConfigurationService, farmId, selectedTab]
  );

  const requestYield = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) =>
      animalConfigurationService.indexYield(farmId, {
        page,
        sort,
        desc,
        ...filter,
        species: selectedTab,
      }),
    [animalConfigurationService, farmId, selectedTab]
  );

  useEffect(() => {
    animalConfigurationService
      .getConfigurations(farmId)
      .subscribe((response: AnimalConfigurationPair) => {
        setConfigurations(response);
      });
  }, [animalConfigurationService, farmId]);

  return (
    <div className={css['modal-overlay']}>
      <div className={css['modal-content']}>
        <label className={css['special-gray']}>
          {t('pages.livestock.animals_and_events.animal_settings.settings')}
        </label>
        <h2 className={css['special']}>
          {t(
            'pages.livestock.animals_and_events.animals.planned_events.events'
          )}
        </h2>
        <div className={css['button-group']}>
          <div className={css['buttons']}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={selectedTab === tab.key ? css['selected'] : ''}
                onClick={() => handleTabChange(tab.key)}
              >
                {t(tab.label)}
              </button>
            ))}
          </div>
          <div className={css['line-container']}>
            <div className={css['line']} />
          </div>
        </div>
        <div className={css['modal-body']}>
          {selectedTab === TabKey.CATTLE && (
            <div className={css['animal-yield-table']}>
              <Table<AnimalConfiguration>
                request={requestYield}
                keyAccessor="id"
                paginate={false}
                columns={[
                  {
                    accessor: 'event_type',
                    header: t(
                      'pages.livestock.animals_and_events.animals.event'
                    ),
                    className: css['strong'],
                    valuable: (value: eventType) => {
                      return value
                        ? t(
                            'pages.livestock.animals_and_events.animal_settings.planned_yield'
                          )
                        : '';
                    },
                  },
                  {
                    accessor: 'event_details',
                    header: t(
                      'pages.livestock.animals_and_events.animal_settings.details'
                    ),
                  },
                  {
                    accessor: 'day_count',
                    header: t(
                      'pages.livestock.animals_and_events.animal_settings.day_count'
                    ),
                    valuable: (value: string, row: AnimalConfiguration) =>
                      renderGoalPicker(row),
                  },
                ]}
              />
            </div>
          )}
          <div className={css['animal-table']}>
            <Table<AnimalConfiguration>
              request={request}
              keyAccessor="id"
              paginate={false}
              columns={[
                {
                  accessor: 'event_type',
                  header: t('pages.livestock.animals_and_events.animals.event'),
                  className: css['strong'],
                  valuable: (value: eventType) => {
                    const eventTypes = {
                      [eventType.SEARCHING]: t(
                        'pages.livestock.animals_and_events.animals.types.searching'
                      ),
                      [eventType.DRY_OFF]: t(
                        'pages.livestock.animals_and_events.animals.types.dry_off'
                      ),
                      [eventType.CALVING]: t(
                        'pages.livestock.animals_and_events.animals.types.calving'
                      ),
                      [eventType.PREGNANCY]: t(
                        'pages.livestock.animals_and_events.animals.types.pregnancy'
                      ),
                    };

                    return (
                      eventTypes[value] ??
                      t('pages.livestock.animals_and_events.animals.event')
                    );
                  },
                },
                {
                  accessor: 'event_details',
                  header: t(
                    'pages.livestock.animals_and_events.animal_settings.details'
                  ),
                },
                {
                  accessor: 'day_count',
                  header: t(
                    'pages.livestock.animals_and_events.animal_settings.day_count'
                  ),
                  valuable: (value: string, row: AnimalConfiguration) =>
                    renderGoalPicker(row),
                },
              ]}
            />
          </div>
        </div>
        <div className={css['modal-actions']}>
          <ButtonComponent
            key={'save-btn'}
            name={'save-btn'}
            styling={ButtonStyling.primary}
            label={t('pages.crop.vaad_liz_reports_v2.notes_modal.save')}
            click={handleSave}
          />
          <ButtonComponent
            key={'cancel-btn'}
            name={'cancel-btn'}
            styling={ButtonStyling.cancel}
            label={t('pages.crop.vaad_liz_reports_v2.notes_modal.cancel')}
            click={closeModal}
          />
        </div>
      </div>
    </div>
  );
}
